import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from './cn';

export type ToastTone = 'released' | 'locked' | 'timer' | 'dispute' | 'neutral';

export interface ToastProps extends HTMLAttributes<HTMLDivElement> {
  tone?: ToastTone;
  icon?: ReactNode;
  /** Uppercase mono second line — tx hash, mechanism, timestamp. */
  detail?: ReactNode;
}

const TONES: Record<ToastTone, string> = {
  released: 'bg-released-soft text-released',
  locked: 'bg-locked-soft text-locked',
  timer: 'bg-timer-soft text-timer',
  dispute: 'bg-dispute-soft text-dispute',
  neutral: 'bg-surface-raised text-fg-2',
};

export function Toast({ tone = 'neutral', icon, detail, className, children, ...rest }: ToastProps) {
  return (
    <div
      role="status"
      className={cn(
        'flex items-center gap-3 px-3.5 py-3 bg-surface-overlay border border-strong shadow-overlay',
        className,
      )}
      {...rest}
    >
      {icon && (
        <span className={cn('w-8 h-8 flex-none inline-flex items-center justify-center', TONES[tone])}>
          {icon}
        </span>
      )}
      <span className="flex flex-col gap-[3px] min-w-0">
        <span className="font-body font-semibold text-[15px] leading-[1.3] text-fg-1">{children}</span>
        {detail && (
          <span className="font-mono text-micro font-medium leading-normal tracking-[0.06em] uppercase text-fg-2">
            {detail}
          </span>
        )}
      </span>
    </div>
  );
}
